import { apiInstance } from '@/api/api.js'
import { authInstance } from '../../services/AuthService'
import { debounce } from '@/helpers/debounce'
import axios from 'axios'
import Config from '@/config.json'
import moment from 'moment-timezone'
import jwt_decode from 'jwt-decode'

const api = apiInstance()
const auth = authInstance()

export default {
  async getProfile({ commit, dispatch }, { profileId }) {
    const url = `/profiles/${profileId}`
    try {
      if (!profileId) return
      const response = await api.get(url)
      const etagVersion = `${response?.headers?.etag || 1}`
      response.data.version = parseInt(
        etagVersion.replace('W/', '').replace('"', ''),
        10,
      )
      commit('updateUser', response.data)
      commit(
        'headersStates/setHeadersEtag',
        { headers: response.headers, url },
        { root: true },
      )
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
    }
  },
  async logoutUser({ commit, dispatch, rootState }) {
    localStorage.removeItem('draftMessage')
    const { refreshToken } = rootState.userStates
    dispatch('revokeToken', refreshToken)

    commit('updateToken', null)
    commit('updateRefreshToken', null)
    commit('updateProfileId', null)
    commit('updateUserTenant', null)
    commit('updateUserFeatureFlags', {})
    commit('updateLoggedIn', false)
    commit('updateUser', {})
    setTimeout(() => {
      auth.logout()
    }, 500)
  },
  async revokeToken(_, refreshToken) {
    const params = new URLSearchParams()
    params.append('token', refreshToken)
    try {
      if (!refreshToken) throw new Error('Unauthorized')
      axios({
        method: 'post',
        url: Config.VUE_APP_CAS_URL + '/revoke?token=' + refreshToken,
        auth: {
          username: Config.VUE_APP_CLIENT_ID,
          password: Config.VUE_APP_CLIENT_SECRET,
        },
      })
        .then(response => {
          console.log(response)
        })
        .catch(error => {
          console.log(error)
        })
    } catch (error) {
      console.error(error)
    }
  },
  async updateProfile({ commit, rootState, dispatch }, { profileId, data }) {
    const url = `/profiles/${profileId}`
    try {
      commit('setLoading', true)
      const { headersStates, userStates } = rootState
      const response = await api.put(url, data, {
        headers: { 'If-Match': headersStates.headersEtag[url] },
      })
      try {
        response.data.version = parseInt(
          response.headers.etag.replace('W/', '').replace('"', ''),
          10,
        )
      } catch {
        response.data.version = 1
      }
      const oldThumbnailUrl = userStates?.user?.thumbnailUrl
      commit('updateUser', { thumbnailUrl: oldThumbnailUrl, ...response.data })
      commit('appStates/setSnackbarNotification', 'User profile updated.', {
        root: true,
      })
    } catch (error) {
      if (error.code == 412 && !data._retry) {
        data._retry = true
        await dispatch('getProfile', { profileId: profileId })
        dispatch('updateProfile', { profileId: profileId, data: data })
      } else {
        dispatch('appStates/setErrorMessage', error, { root: true })
      }
      commit('setLoading', false)
      throw error
    }
  },
  async updateProfilePicture(
    { commit, rootState, dispatch },
    { profileId, file, retry },
  ) {
    const url = `/profiles/${profileId}/picture`
    const profileUrl = `/profiles/${profileId}`
    try {
      commit('setLoading', true)
      const { headersStates } = rootState

      let formData = new FormData()
      if (file) {
        formData.append('file', file.file, file.name)
      }
      await api.put(url, formData, {
        headers: {
          'If-Match': headersStates.headersEtag[profileUrl],
          'Content-Type': 'multipart/form-data',
        },
      })
      dispatch('getProfile', { profileId: profileId })
      commit('appStates/setSnackbarNotification', 'User profile updated.', {
        root: true,
      })
    } catch (error) {
      if (error.code == 412 && !retry) {
        await dispatch('getProfile', { profileId: profileId })
        dispatch('updateProfilePicture', {
          profileId: profileId,
          file: file,
          retry: true,
        })
      } else {
        dispatch('appStates/setErrorMessage', error, { root: true })
      }
      commit('setLoading', false)
    }
  },
  async getProfileQuickMessages({ dispatch }, { profileId }) {
    const url = `/profiles/${profileId}/quickMessages`

    try {
      const response = await api.get(url)
      return response.data
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
    }
  },
  async addProfileQuickMessages({ commit, dispatch }, { profileId, data }) {
    const url = `/profiles/${profileId}/quickMessages`

    try {
      const response = await api.post(url, { message: data })
      commit(
        'appStates/setSnackbarNotification',
        response.message || 'Quick message added.',
        { root: true },
      )
      return response.data
    } catch (error) {
      dispatch(
        'appStates/setErrorMessage',
        error.message || 'Unexpected error while adding message',
        { root: true },
      )
    }
  },
  async updateProfileQuickMessage(
    { commit, dispatch },
    { profileId, quickMessageId, data, headerVersion },
  ) {
    const url = `profiles/${profileId}/quickMessages/${quickMessageId}`

    try {
      const response = await api.put(
        url,
        { message: data },
        {
          headers: {
            'If-Match': `"${headerVersion}"`,
          },
        },
      )
      commit(
        'appStates/setSnackbarNotification',
        response.message || 'Quick message updated.',
        { root: true },
      )
      return response.data
    } catch (error) {
      dispatch(
        'appStates/setErrorMessage',
        error.message || 'Unexpected error while updating message',
        { root: true },
      )
    }
  },
  async deleteProfileQuickMessage(
    { commit, dispatch },
    { profileId, quickMessageId, headerVersion },
  ) {
    const url = `profiles/${profileId}/quickMessages/${quickMessageId}`

    try {
      const response = await api.delete(url, {
        headers: {
          'If-Match': `"${headerVersion}"`,
        },
      })
      commit(
        'appStates/setSnackbarNotification',
        response.message || 'Quick message deleted.',
        { root: true },
      )
      return response.data
    } catch (error) {
      dispatch(
        'appStates/setErrorMessage',
        error.message || 'Unexpected error while deleting message',
        { root: true },
      )
    }
  },
  async sendNewUserEmail({ commit, dispatch }, { profileId }) {
    const url = `/profiles/${profileId}/sendNewUserEmail`
    try {
      commit('setLoading', true)
      await api.post(url)
      commit('appStates/setSnackbarNotification', 'New user email sent!', {
        root: true,
      })
      commit('setLoading', false)
    } catch (error) {
      commit('setLoading', false)
      dispatch('appStates/setErrorMessage', error, { root: true })
    }
  },
  async getTasks({ commit, dispatch }) {
    const url = `/tasks`
    try {
      const response = await api.get(url)
      commit('updateTasks', response.data)
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
    }
  },
  async deleteTask({ dispatch }, taskId) {
    const url = `/tasks/${taskId}`
    try {
      await api.delete(url)
      dispatch('getTasks')
    } catch (error) {
      dispatch(
        'appStates/setErrorMessage',
        { ...error, withoutRetry: true },
        { root: true },
      )
      dispatch('getTasks')
    }
  },
  async getNotifications(
    { commit, dispatch, state },
    { isAdvisor = false },
    params = new URLSearchParams(),
  ) {
    const url = `/notification-service/notifications`
    if (!params) params = new URLSearchParams()
    if (!params.has('pageSize')) params.append('pageSize', 100)
    if (!params.has('filter') && isAdvisor) params.append('filter', 'unread')
    try {
      const response = await api.get(url, { params })
      if (!isAdvisor) {
        commit('updateNotifications', response.data)
        commit(
          'profilePreviewsStates/setProfilePreviews',
          response.data.profilePreviews,
          {
            root: true,
          },
        )
      }
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
    }
  },
  async getNotificationsDebounced({ dispatch }, params = {}) {
    debounce(
      () => dispatch('getNotifications', params),
      2000,
      'getNotifications',
    )
  },
  async getNotificationsGrouped(
    { commit, dispatch },
    params = new URLSearchParams(),
  ) {
    const url = `/notification-service/notifications-grouped`
    if (!params) params = new URLSearchParams()
    if (!params.has('filter')) params.append('filter', 'all')
    try {
      const response = await api.get(url, { params })
      const content = response.data.content
      const profilePrev = content
        .filter(
          (el, index, self) =>
            !!el?.profile &&
            self.findIndex(
              prev => prev?.profile?.profileId === el?.profile?.profileId,
            ) === index,
        )
        .map(el => el.profile)
      commit('updateNotifications', response.data)
      if (params.get('filter') === 'all') {
        commit('updateNotificationsGroupedFirstRender', response.data)
      }

      commit('profilePreviewsStates/setProfilePreviews', profilePrev, {
        root: true,
      })
      return response.data
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
    }
  },
  async getNotificationsGroupedByParticipantId(
    { commit, dispatch, state },
    { id, pageSize = 5, pageLastItem, tab = 0 },
    params = new URLSearchParams(),
  ) {
    const url = `/notification-service/notifications-grouped/${id}`
    if (!params) params = new URLSearchParams()
    if (!params.has('pageSize') && pageSize !== undefined)
      params.append('pageSize', pageSize)
    if (!params.has('lastItem') && pageLastItem !== undefined)
      params.append('lastItem', pageLastItem)
    if (!params.has('filter'))
      params.append('filter', tab === 0 ? 'all' : tab === 1 ? '3d' : '1d')

    try {
      const response = await api.get(url, { params })
      const content = state.notifications.content
      const participantNotifications = content.map((el, index, self) =>
        !!el?.profile &&
        self.findIndex(
          prev =>
            prev?.profile?.profileId === response.data?.profile?.profileId,
        ) === index
          ? {
              ...response.data,
              notification: el.notification,
              notifications:
                pageLastItem !== undefined
                  ? [...el.notifications, ...response.data.notifications]
                  : response.data.notifications,
            }
          : el,
      )
      commit('updateNotifications', { content: participantNotifications })
      return response
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
    }
  },
  async getNotificationsGroupedDebounced({ dispatch }, params) {
    debounce(
      () => dispatch('getNotificationsGrouped', params),
      2000,
      'getNotificationsGrouped',
    )
  },
  async readNotificationGrouped({ dispatch }, { id, tab = 0 }) {
    const url = `/notification-service/notifications-grouped/${id}/readall`
    try {
      await api.post(url)
      const defaultQueryParams = new URLSearchParams()
      defaultQueryParams.append(
        'filter',
        tab === 0 ? 'all' : tab === 1 ? '3d' : '1d',
      )
      dispatch('getNotificationsGrouped')
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
    }
  },
  async readNotification(
    { commit, dispatch, state },
    { id, unread = false, resendAt, isAdvisor = false, tab = 0, participantId },
  ) {
    const url = `/notification-service/notifications/${id}`
    try {
      const response = await api.put(url, { unread: unread, resendAt })

      if (isAdvisor && participantId) {
        const defaultQueryParams = new URLSearchParams()
        defaultQueryParams.append(
          'filter',
          tab === 0 ? 'all' : tab === 1 ? '3d' : '1d',
        )
        const res = await dispatch(
          'getNotificationsGroupedByParticipantId',
          { id: participantId },
          defaultQueryParams,
        )
        const content = state.notificationsGroupedFirstRender.content
        const participantNotifications = content.map((el, index, self) =>
          !!el?.profile &&
          self.findIndex(
            prev => prev?.profile?.profileId === res.data?.profile?.profileId,
          ) === index
            ? {
                ...res.data,
                notification: el.notification,
                notifications: res.data.notifications,
              }
            : el,
        )
        commit('updateNotificationsGroupedFirstRender', {
          content: participantNotifications,
        })
      } else {
        dispatch('getNotifications', { isAdvisor })
      }

      if (response.status === 200 && resendAt) {
        commit(
          'appStates/setSnackbarNotification',
          `Next notification scheduled to ${moment(resendAt).format(
            'MM-DD-YYYY',
          )}`,
          {
            root: true,
          },
        )
      }
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
    }
  },
  async readNotificationsAll({ dispatch }, { isAdvisor = false }) {
    const url = `/notification-service/notifications/readall`
    await api.post(url)
    try {
      if (!isAdvisor) {
        dispatch('getNotifications', { isAdvisor })
      } else {
        dispatch('getNotificationsGrouped')
      }
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
    }
  },
  async getNotificationsSettings({ commit, dispatch }) {
    const url = `/notification-service/notifications/settings/web`
    try {
      const response = await api.get(url)
      commit('updateNotificationsSettings', response.data)
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
    }
  },
  async saveNotificationsSettings({ dispatch }, data) {
    const url = `/notification-service/notifications/settings/web`
    try {
      await api.put(url, data)
      dispatch('getNotificationsSettings')
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
    }
  },
  showNotification(message) {
    let notification = new Notification('KeepRising.io', {
      body: message.message,
      tag: 'KeepRising',
      icon: require('@/assets/kr.png'),
      data: message.link,
    })
    notification.onclick = function(e) {
      window.location.href = e.target.data
    }
  },
  async toggleActivationAccount(
    { commit, dispatch },
    { url, isActive, version },
  ) {
    try {
      commit('setLoading', true)
      await api.put(
        url,
        { isActive: isActive },
        { headers: { 'If-Match': 'W/"' + version + '"' } },
      )

      let message = isActive ? 'activated' : 'inactivated'
      commit('appStates/setSnackbarNotification', 'User ' + message, {
        root: true,
      })
      commit('setLoading', false)
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
    }
  },
  async deleteUser({ commit, rootState, dispatch }, { url }) {
    try {
      const { headersStates } = rootState

      commit('setLoading', true)

      await api.delete(url, {
        headers: { 'If-Match': headersStates.headersEtag[url] },
      })

      commit('setLoading', false)
      commit('appStates/setSnackbarNotification', 'User was deleted!', {
        root: true,
      })
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
    }
  },
}
